/**
 * Client — view assigned program by week → day, enter weight+reps, tap Finish day.
 * Finishing a day creates a programSession record which the coach sees in their activity feed.
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  getProgram,
  getProgramForClient,
  getProgramExercises,
  getWorkoutLogs,
  logWorkout,
  updateWorkoutLog,
  finishProgramDay,
  getMyProgramSessions,
  getActiveCoachRelationship,
} from '../../domain/usecases/coaching';
import Card from '../../shared/ui/Card.jsx';
import Stat from '../../shared/ui/Stat.jsx';
import Badge from '../../shared/ui/Badge.jsx';
import Button from '../../shared/ui/Button.jsx';
import ProgressBar from '../../shared/ui/ProgressBar.jsx';
import Display from '../../shared/ui/Display.jsx';
import Title from '../../shared/ui/Title.jsx';
import Body from '../../shared/ui/Body.jsx';
import Label from '../../shared/ui/Label.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import styles from './ProgramViewPage.module.css';

function dayKey(week, day) {
  return `${week}:${day}`;
}

export function ProgramViewPage() {
  const { programId } = useParams();
  const { userId: clientId } = useAuth();
  const [program, setProgram] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [logs, setLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [coachId, setCoachId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingId, setLoggingId] = useState(null);
  const [finishingKey, setFinishingKey] = useState(null);

  useEffect(() => {
    if (!programId) return;
    setLoading(true);
    const loadProgram = clientId ? getProgramForClient(clientId, programId) : getProgram(programId);
    Promise.all([
      loadProgram,
      getProgramExercises(programId),
      clientId ? getWorkoutLogs(clientId, programId) : Promise.resolve([]),
      clientId ? getMyProgramSessions(clientId, programId) : Promise.resolve([]),
      clientId ? getActiveCoachRelationship(clientId) : Promise.resolve(null),
    ]).then(([p, ex, l, s, rel]) => {
      setProgram(p);
      setExercises(ex);
      setLogs(l);
      setSessions(s);
      setCoachId(rel?.coachId || p?.coachId || null);
      setLoading(false);
    });
  }, [programId, clientId]);

  const getLogForExercise = (exerciseId) => logs.find((l) => l.programExerciseId === exerciseId);
  const isDayFinished = (week, day) => sessions.some((s) => s.weekNumber === week && (s.dayNumber || 1) === day);

  async function handleLog(exerciseId, patch) {
    if (!clientId) return;
    setLoggingId(exerciseId);
    try {
      const existing = getLogForExercise(exerciseId);
      const completedAt = patch.completed ? new Date() : null;
      if (existing?.id) {
        const update = {};
        if (patch.actualWeight !== undefined) update.actualWeight = patch.actualWeight === null ? null : Number(patch.actualWeight);
        if (patch.actualReps !== undefined) update.actualReps = patch.actualReps === null ? null : Number(patch.actualReps);
        if (patch.completed !== undefined) {
          update.completed = !!patch.completed;
          update.completedAt = completedAt;
        }
        await updateWorkoutLog(existing.id, update);
      } else {
        await logWorkout({
          clientId,
          programId,
          programExerciseId: exerciseId,
          actualWeight: patch.actualWeight == null ? null : Number(patch.actualWeight),
          actualReps: patch.actualReps == null ? null : Number(patch.actualReps),
          completed: !!patch.completed,
          completedAt,
        });
      }
      const l = await getWorkoutLogs(clientId, programId);
      setLogs(l);
    } finally {
      setLoggingId(null);
    }
  }

  async function handleFinishDay(week, day, dayExercises) {
    if (!clientId || !coachId) return;
    if (!window.confirm(`Finish Week ${week} · Day ${day}? Your coach will be notified.`)) return;
    setFinishingKey(dayKey(week, day));
    try {
      await finishProgramDay({
        clientId,
        coachId,
        programId,
        programTitle: program?.title || '',
        weekNumber: week,
        dayNumber: day,
        exerciseCount: dayExercises.length,
      });
      const s = await getMyProgramSessions(clientId, programId);
      setSessions(s);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Could not finish day.');
    } finally {
      setFinishingKey(null);
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.skeletonList}>
          <Skeleton width="60%" height={44} radius="var(--radius-sm)" />
          <Skeleton width="100%" height={80} radius="var(--radius-md)" />
          <Skeleton width="100%" height={80} radius="var(--radius-md)" />
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className={styles.page}>
        <Body as="p">Program not found.</Body>
        <Link to="/coaching/my-programs">Back</Link>
      </div>
    );
  }

  // Group exercises by week -> day
  const byWeekDay = {};
  exercises.forEach((ex) => {
    const w = ex.weekNumber || 1;
    const d = ex.dayNumber || 1;
    if (!byWeekDay[w]) byWeekDay[w] = {};
    if (!byWeekDay[w][d]) byWeekDay[w][d] = [];
    byWeekDay[w][d].push(ex);
  });

  const totalExercises = exercises.length;
  const completedExercises = exercises.filter((ex) => getLogForExercise(ex.id)?.completed).length;

  return (
    <div className={styles.page}>
      <Button variant="ghost" size="sm" as={Link} to="/coaching/my-programs" style={{ marginBottom: '1rem' }}>
        ← My programs
      </Button>

      <Display as="h1" size="l" style={{ marginBottom: '0.5rem' }}>{program.title}</Display>
      {program.description && (
        <Body tone="secondary" style={{ marginBottom: '1rem' }}>{program.description}</Body>
      )}

      {totalExercises > 0 && (
        <div className={styles.progress}>
          <ProgressBar value={completedExercises} max={totalExercises} />
          <Body tone="secondary" as="p" style={{ fontSize: 13, marginTop: 4 }}>
            {completedExercises} of {totalExercises} exercises completed
          </Body>
        </div>
      )}

      {program.weeks && Array.from({ length: program.weeks }, (_, i) => i + 1).map((week) => {
        const weekMap = byWeekDay[week] || {};
        const dayNumbers = Object.keys(weekMap).map(Number).sort((a, b) => a - b);
        return (
          <div key={week} className={styles.weekBlock}>
            <Title as="h2" style={{ fontSize: 16, marginBottom: 12 }}>Week {week}</Title>

            {dayNumbers.length === 0 && (
              <Body tone="secondary">No exercises for this week.</Body>
            )}

            {dayNumbers.map((day) => {
              const dayExercises = weekMap[day] || [];
              const finished = isDayFinished(week, day);
              const finishing = finishingKey === dayKey(week, day);
              const dayLoggedCount = dayExercises.filter((ex) => getLogForExercise(ex.id)?.completed).length;

              return (
                <div key={day} className={styles.dayBlock}>
                  <div className={styles.dayHeader}>
                    <Body as="span" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)' }}>
                      Day {day}
                    </Body>
                    {finished && <Badge variant="success">Finished</Badge>}
                  </div>

                  <ul className={styles.exList}>
                    {dayExercises.map((ex) => {
                      const log = getLogForExercise(ex.id);
                      const prescribedLabel = [
                        ex.sets && ex.reps ? `${ex.sets}×${ex.reps}` : null,
                        ex.targetWeight != null ? `@ ${ex.targetWeight} kg` : null,
                      ].filter(Boolean).join(' ');

                      return (
                        <li key={ex.id}>
                          <Card padding="sm" className={styles.exCard}>
                            <div className={styles.exHeader}>
                              <Title as="h3" style={{ fontSize: 15, lineHeight: '20px' }}>
                                {ex.exerciseName}
                              </Title>
                              {log?.completed ? <Badge variant="success">Logged</Badge> : null}
                            </div>

                            {prescribedLabel && (
                              <Stat
                                label="PRESCRIBED"
                                value={prescribedLabel}
                                style={{ marginTop: 8 }}
                              />
                            )}

                            <div className={styles.logRow}>
                              <div className={styles.inputGroup}>
                                <Label as="label" style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>
                                  Weight (kg)
                                </Label>
                                <input
                                  type="number"
                                  placeholder="kg"
                                  defaultValue={log?.actualWeight ?? ''}
                                  onBlur={(e) => {
                                    const v = e.target.value;
                                    if (v !== '' || log) handleLog(ex.id, { actualWeight: v === '' ? null : v });
                                  }}
                                  className={styles.inputNum}
                                  disabled={finished}
                                />
                              </div>
                              <div className={styles.inputGroup}>
                                <Label as="label" style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>
                                  Reps
                                </Label>
                                <input
                                  type="number"
                                  placeholder="reps"
                                  defaultValue={log?.actualReps ?? ''}
                                  onBlur={(e) => {
                                    const v = e.target.value;
                                    if (v !== '' || log) handleLog(ex.id, { actualReps: v === '' ? null : v });
                                  }}
                                  className={styles.inputNum}
                                  disabled={finished}
                                />
                              </div>
                              {!log?.completed ? (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  disabled={!!loggingId || finished}
                                  onClick={() => handleLog(ex.id, { completed: true })}
                                >
                                  {loggingId === ex.id ? 'Saving…' : 'Log set'}
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={!!loggingId || finished}
                                  onClick={() => handleLog(ex.id, { completed: false })}
                                >
                                  Undo
                                </Button>
                              )}
                            </div>

                            {log?.coachNote && (
                              <div className={styles.coachNote}>
                                <Label>COACH NOTE</Label>
                                <Body tone="secondary" style={{ fontSize: 13 }}>{log.coachNote}</Body>
                              </div>
                            )}
                          </Card>
                        </li>
                      );
                    })}
                  </ul>

                  <div className={styles.dayFooter}>
                    <Body tone="secondary" as="span" style={{ fontSize: 13 }}>
                      {dayLoggedCount} of {dayExercises.length} exercises logged
                    </Body>
                    {finished ? (
                      <Badge variant="gold">Day finished</Badge>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={finishing || dayExercises.length === 0 || !coachId}
                        onClick={() => handleFinishDay(week, day, dayExercises)}
                      >
                        {finishing ? 'Finishing…' : 'Finish day'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
